import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { H1, H2, Body } from '../components/ui/Typography';
import ArticleStructuredData from '../components/common/ArticleStructuredData';

export default function PressRelease6() {
  const title = "RentFi Makes Real Estate Investment Available to Everyone";
  const description = "RentFi has successfully launched its blockchain-based real estate investment platform, making property investment as simple as buying a token. Through its $RENT token, now actively trading, investors worldwide can earn rental income without the traditional hurdles of property ownership.";
  const publishDate = "2025-02-05T00:00:00+00:00";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content="RentFi launch, RENT token, blockchain real estate, property investment, passive income, token launch" />
        <link rel="canonical" href="https://rentfi.io/press/real-estate-investment-for-everyone" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://rentfi.io/press/real-estate-investment-for-everyone" />
        <meta property="og:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        <meta property="article:published_time" content={publishDate} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RentFi_io" />
        <meta name="twitter:creator" content="@RentFi_io" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        
        {/* Article-specific Meta Tags */}
        <meta name="article:section" content="Press Releases" />
        <meta name="article:tag" content="Token Launch, Real Estate Investment, Blockchain Technology, REIT" />
      </Helmet>

      <ArticleStructuredData
        title={title}
        description={description}
        publishDate={publishDate}
      />

      <main className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/press"
              className="inline-flex items-center text-primary hover:text-primary-dark mb-8 group"
              aria-label="Return to press releases list"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Press Releases
            </Link>

            <article className="bg-white rounded-xl shadow-lg p-8">
              <header className="mb-8">
                <time dateTime="2025-02-05" className="text-gray-500 mb-4 block">February 5, 2025</time>
                <H1 className="text-primary mb-4">RentFi Makes Real Estate Investment Available to Everyone</H1>
                <address className="text-gray-500 italic mb-8 not-italic">ROAD TOWN, BVI</address>
              </header>

              <div className="prose max-w-none">
                <Body className="text-gray-600 mb-6">
                  RentFi has successfully launched its blockchain-based real estate investment platform, making property investment as simple as buying a token. Through its $RENT token, now actively trading, investors worldwide can earn rental income without the traditional hurdles of property ownership.
                </Body>

                <Body className="text-gray-600 mb-6">
                  "Real estate investment has always been profitable but often unreachable for most people. We've changed that by making it as easy as buying any other digital asset," explains the RentFi Foundation. "Anyone with a smartphone can now invest in real estate and earn rental income."
                </Body>

                <H2 className="text-primary mb-4">Platform Innovations</H2>
                <Body className="text-gray-600 mb-6">
                  RentFi's platform transforms how people invest in property through several key innovations:
                </Body>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">The platform distributes rental income in two ways: 50% goes directly to token holders as regular passive income, while the other 50% is used for token buybacks and burns, helping increase token value over time.</li>
                  <li className="text-gray-600">Built on the Solana blockchain, RentFi ensures that transactions are fast and affordable. The platform has set a maximum supply of 100 million tokens, and implementing a deflationary system that supports long-term value growth.</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  "Traditional real estate investment typically requires large down payments, complex paperwork, and ongoing property management," says the RentFi Foundation. "Our platform eliminates these barriers. Token holders can start earning rental income without dealing with tenants, maintenance, or legal complexities."
                </Body>

                <H2 className="text-primary mb-4">Key Benefits</H2>
                <Body className="text-gray-600 mb-6">
                  The project's innovative approach provides several benefits for investors:
                </Body>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">Access to a diverse property portfolio, reducing the risks typically associated with single-property investments</li>
                  <li className="text-gray-600">Regular rental income distributed automatically to token holders</li>
                  <li className="text-gray-600">No property management responsibilities</li>
                  <li className="text-gray-600">Easy entry and exit through token trading</li>
                  <li className="text-gray-600">Potential for token value appreciation through systematic buybacks</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  RentFi marks a significant step forward in making real estate investment accessible to everyone. Through its global portfolio, the platform combines the stability of property investment with the convenience of digital assets, creating new opportunities for both experienced investors and newcomers to the real estate market.
                </Body>

                <Body className="text-gray-600 mb-6">
                  Investors interested in participating can now purchase $RENT tokens through major cryptocurrency exchanges. For more information about RentFi and its innovative approach to real estate investment, visit{' '}
                  <a 
                    href="https://rentfi.io" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit RentFi's official website for more information"
                  >
                    rentfi.io
                  </a>{' '}
                  or follow on X:{' '}
                  <a 
                    href="https://x.com/RentFi_io" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Follow RentFi on X (Twitter) for updates"
                  >
                    @RentFi_io
                  </a>
                </Body>

                <H2 className="text-primary mb-4">About RentFi</H2>
                <Body className="text-gray-600 mb-6">
                  RentFi Limited, the first-ever Real Estate Investment Trust (REIT) on blockchain, is revolutionizing property investment by making it accessible to everyone. By combining traditional real estate with blockchain technology, RentFi creates new opportunities for global investors to earn rental income without the complexities of direct property ownership.
                </Body>

                <H2 className="text-primary mb-4">Media Contact</H2>
                <Body className="text-gray-600">
                  Block Consulting —{' '}
                  <a 
                    href="https://blockconsulting.cc" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit Block Consulting's website"
                  >
                    https://blockconsulting.cc
                  </a>
                </Body>
              </div>
            </article>
          </motion.div>
        </div>
      </main>
    </>
  );
}